import {
  getGridDateOperators,
  GridCellParams,
  GridFilterInputMultipleValueProps,
  GridFilterInputValue,
  GridFilterItem,
  GridFilterOperator,
} from '@mui/x-data-grid';
import dayjs from 'dayjs';
import {
  AppointmentStatus,
  BillingStatus,
  BiologicalGender,
  CreditCardStatus,
  CustodyType,
  InsuranceProvider,
  InsuranceStatus,
  Locations,
  Migrated,
  OrganizationTypes,
  Positions,
  Providers,
  Regions,
  Roles,
  StatusYesNo,
  UserStatus,
  USStates,
} from './dropDownList';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Box,
  Checkbox,
  FormControl,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material';
import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { useProviders } from './useProviders';

export const PreFilterOption: GridFilterOperator = {
  label: 'Select Status',
  value: 'none',
  getApplyFilterFn: () => null,
};
export const DefualtFilterOption: GridFilterOperator = {
  label: 'Other',
  value: 'other',
  InputComponent: GridFilterInputValue,
  getApplyFilterFn: (filterItem: GridFilterItem) => {
    if (!filterItem.value || String(filterItem.value).trim() === '') return null;
    const needle = String(filterItem.value).toLowerCase();
    return (params: GridCellParams) =>
      String(params.value ?? '')
        .toLowerCase()
        .includes(needle);
  },
};

// Date Picker Input Component for single dates
const GridFilterInputDatePicker = (props: any) => {
  const { item, applyValue } = props;
  const handleChange = (newValue: dayjs.Dayjs | null) => {
    applyValue({ ...item, value: newValue ? newValue.format('MM/DD/YYYY') : '' });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        value={item.value ? dayjs(item.value, 'MM/DD/YYYY') : null}
        onChange={handleChange}
        format='MM/DD/YYYY'
      />
    </LocalizationProvider>
  );
};
const GridFilterInputDateRange = (props: any) => {
  const { item, applyValue } = props;

  const handleStartChange = (newValue: dayjs.Dayjs | null) => {
    applyValue({
      ...item,
      value: [newValue ? newValue.format('MM/DD/YYYY') : '', item.value?.[1] || ''],
    });
  };

  const handleEndChange = (newValue: dayjs.Dayjs | null) => {
    applyValue({
      ...item,
      value: [item.value?.[0] || '', newValue ? newValue.format('MM/DD/YYYY') : ''],
    });
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 1,
        minWidth: 300, // 🔑 critical
        alignItems: 'center',
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label='Start Date'
          value={item.value?.[0] ? dayjs(item.value[0], 'MM/DD/YYYY') : null}
          onChange={handleStartChange}
          format='MM/DD/YYYY'
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
            },
          }}
        />
      </LocalizationProvider>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label='End Date'
          value={item.value?.[1] ? dayjs(item.value[1], 'MM/DD/YYYY') : null}
          onChange={handleEndChange}
          format='MM/DD/YYYY'
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

export const DateFilter: GridFilterOperator[] = getGridDateOperators().filter(
  (operator) =>
    operator.value === 'isNotEmpty' ||
    operator.value === 'isEmpty' ||
    operator.value === 'onOrBefore' ||
    operator.value === 'onOrAfter' ||
    operator.value === 'after' ||
    operator.value === 'not' ||
    operator.value === 'is'
);

export const GenderFilter: GridFilterOperator[] = [
  PreFilterOption,
  ...BiologicalGender.map((gender) => ({
    label: gender.value,
    value: gender.value,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params: GridCellParams) => params.value === gender.value;
    },
  })),
];

export const MigratedFilter: GridFilterOperator[] = [
  PreFilterOption,
  ...Migrated.map((migratedStatus) => ({
    label: migratedStatus.name,
    value: migratedStatus.value,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params: GridCellParams) => params.value === migratedStatus.value;
    },
  })),
];

export const AgeFilter: GridFilterOperator[] = [
  PreFilterOption,
  {
    label: '< than 4',
    value: 'lt4',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params) => typeof params.value === 'number' && params.value < 4;
    },
  },
  {
    label: '4 - 10',
    value: 'between4-10',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params) =>
        typeof params.value === 'number' && params.value >= 4 && params.value <= 10;
    },
  },
  {
    label: '11 - 13',
    value: 'between11-13',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params) =>
        typeof params.value === 'number' && params.value >= 11 && params.value <= 13;
    },
  },
  {
    label: '14 - 17',
    value: 'between14-17',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params) =>
        typeof params.value === 'number' && params.value >= 14 && params.value <= 17;
    },
  },
  {
    label: '18 - 21',
    value: 'between18-21',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params) =>
        typeof params.value === 'number' && params.value >= 18 && params.value <= 21;
    },
  },
  {
    label: '21 - 25',
    value: 'between21-25',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params) =>
        typeof params.value === 'number' && params.value >= 21 && params.value <= 25;
    },
  },

  {
    label: '> than 25',
    value: 'lt25',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params) => typeof params.value === 'number' && params.value > 25;
    },
  },
];
export const OrganizationTypeFilter: GridFilterOperator[] = [
  PreFilterOption,
  ...OrganizationTypes.map((organization) => ({
    label: organization.name,
    value: organization.value,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params: GridCellParams) => params.value === organization.value;
    },
  })),
];

export const UserStatusTypeFilter: GridFilterOperator[] = [
  PreFilterOption,
  ...UserStatus.map((status) => ({
    label: status,
    value: status,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params: GridCellParams) => params.value === status;
    },
  })),
];

export const CustodyTypeFilter: GridFilterOperator[] = [
  PreFilterOption,
  ...CustodyType.map((custodyType) => ({
    label: custodyType.label,
    value: custodyType.label,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params: GridCellParams) => params.value === custodyType.label;
    },
  })),
];

export const RegionFilter: GridFilterOperator[] = [
  PreFilterOption,
  ...Regions.map((region) => ({
    label: region.name,
    value: region.value,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params: GridCellParams) => params.value === region.value;
    },
  })),
];

export const StateFilter: GridFilterOperator[] = [
  PreFilterOption,
  ...USStates.map((state) => ({
    label: state.name,
    value: state.value,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params: GridCellParams) => params.value === state.value;
    },
  })),
];

export const CreditCardStatusFilter: GridFilterOperator[] = [
  PreFilterOption,
  ...CreditCardStatus.map((status) => ({
    label: status.name,
    value: status.value,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params: GridCellParams) => params.value === status.value;
    },
  })),
];

export const StatusYesNoFilter: GridFilterOperator[] = [
  PreFilterOption,
  ...StatusYesNo.map((status) => ({
    label: status.name,
    value: status.value,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params: GridCellParams) => params.value === status.value;
    },
  })),
];

export const AppointmentStatusFilter: GridFilterOperator[] = [
  PreFilterOption,
  ...AppointmentStatus.map((status) => ({
    label: status,
    value: status,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params: GridCellParams) => params.value === status;
    },
  })),
  DefualtFilterOption,
];

export const InsuranceProviderFilter: GridFilterOperator[] = [
  PreFilterOption,
  ...InsuranceProvider.map((insurance) => ({
    label: insurance,
    value: insurance,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params: GridCellParams) => params.value === insurance;
    },
  })),
  DefualtFilterOption,
];

export const BillingStatusFilter: GridFilterOperator[] = [
  PreFilterOption,
  ...BillingStatus.map((billingStatus) => ({
    label: billingStatus.name,
    value: billingStatus.value,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params: GridCellParams) => params.value === billingStatus.value;
    },
  })),
];

export const CustodyFilter: GridFilterOperator[] = [
  PreFilterOption,
  ...CustodyType.map((billingStatus) => ({
    label: billingStatus.label,
    value: billingStatus.key,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params: GridCellParams) => params.value === billingStatus.key;
    },
  })),
  DefualtFilterOption,
];

export const DateTimeFilterOperators: GridFilterOperator[] = [
  {
    label: 'On Date',
    value: 'On Date',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.value) return null;
      const filterDate = dayjs(filterItem.value, 'MM/DD/YYYY');
      return (params) => {
        const cellDate = dayjs(params.value);
        const isSame = cellDate.isSame(filterDate, 'day');
        return isSame;
      };
    },
    InputComponent: GridFilterInputDatePicker,
  },
  {
    label: 'Before',
    value: 'before',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.value) return null;
      const filterDate = dayjs(filterItem.value, 'MM/DD/YYYY').endOf('day');
      return (params) => dayjs(params.value).isBefore(filterDate);
    },
    InputComponent: GridFilterInputDatePicker,
  },
  {
    label: 'After',
    value: 'after',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.value) return null;
      const filterDate = dayjs(filterItem.value, 'MM/DD/YYYY').startOf('day');
      return (params) => dayjs(params.value).isAfter(filterDate);
    },
    InputComponent: GridFilterInputDatePicker,
  },
  {
    label: 'In Range',
    value: 'inRange',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.value || !filterItem.value[0] || !filterItem.value[1]) return null;
      const startDate = dayjs(filterItem.value[0], 'MM/DD/YYYY').startOf('day');
      const endDate = dayjs(filterItem.value[1], 'MM/DD/YYYY').endOf('day');
      return (params) => {
        const cellDate = dayjs(params.value);
        return cellDate.isAfter(startDate) && cellDate.isBefore(endDate);
      };
    },
    InputComponent: GridFilterInputDateRange,
    // For date range, I have usedcustom InputComponent
    // with two-date picker component
  },
];

export const PositionFilter: GridFilterOperator[] = [
  PreFilterOption,
  ...Positions.map((v) => ({
    label: v.position,
    value: v.position,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params: GridCellParams) => params.value === v.position;
    },
  })),
  DefualtFilterOption,
];

export const RolesFilter: GridFilterOperator[] = [
  PreFilterOption,
  ...Roles.map((v) => ({
    label: v.role,
    value: v.role,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params: GridCellParams) => params.value === v.role;
    },
  })),
  DefualtFilterOption,
];

export const PaymentFilter: GridFilterOperator[] = [
  PreFilterOption,
  ...InsuranceStatus.map((pay) => ({
    label: pay.name,
    value: pay.value,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params: GridCellParams) => params.value === pay.value;
    },
  })),
];

export const LocationFilter: GridFilterOperator[] = [
  PreFilterOption,
  ...Locations.map((v) => ({
    label: v.filterValue,
    value: v.location,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params: GridCellParams) =>
        String(params?.value)
          ?.toLowerCase()
          ?.includes(v.filterValue.toLowerCase());
    },
  })),
];
const InputNumberRange = ({ item, applyValue }: any) => {
  const [range, setRange] = React.useState<[number | '', number | '']>(() => {
    const [from = '', to = ''] = (item.value as [number, number]) || [];
    return [from, to];
  });

  const handleChange = (index: 0 | 1) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value === '' ? '' : Number(e.target.value);
    const updatedRange: [number | '', number | ''] = [...range];
    updatedRange[index] = val;
    setRange(updatedRange);
    applyValue({ ...item, value: updatedRange });
  };

  return (
    <span>
      <FormControl>
        <InputLabel htmlFor='to'>Start</InputLabel>
        <Input
          type='number'
          sx={{ paddingLeft: '4px', paddingRight: '8px' }}
          value={range[0]}
          onChange={handleChange(0)}
        />
      </FormControl>

      <FormControl>
        <InputLabel htmlFor='to'>End</InputLabel>
        <Input
          type='number'
          sx={{ paddingLeft: '4px', paddingRight: '8px' }}
          value={range[1]}
          onChange={handleChange(1)}
        />
      </FormControl>
    </span>
  );
};
export const ScoreFilter: GridFilterOperator[] = [
  PreFilterOption,
  {
    label: 'Less than',
    value: 'lessThan',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (filterItem.value == null || isNaN(Number(filterItem.value))) return null;
      const val = Number(filterItem.value);
      return (params) => typeof params.value === 'number' && params.value < val;
    },
    InputComponent: GridFilterInputValue,
  },

  {
    label: 'Greater than',
    value: 'greaterThan',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (filterItem.value == null || isNaN(Number(filterItem.value))) return null;
      const val = Number(filterItem.value);
      return (params) => typeof params.value === 'number' && params.value > val;
    },
    InputComponent: GridFilterInputValue,
  },
  {
    label: 'Between',
    value: 'between',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      const min = isNaN(filterItem.value[0]);
      const max = isNaN(filterItem.value[1]);
      if (min || max) return null;
      return (params) => {
        const value = isNaN(params.value);
        return value >= min && value <= max;
      };
    },
    InputComponent: InputNumberRange, // Or your custom range input component
  },
  DefualtFilterOption,
];
// Custom styled Select component
const StyledSelect = styled(Select)({
  '& .MuiSelect-select': {
    padding: '8px 32px 8px 12px',
    backgroundColor: '#ffffff',
    borderRadius: '4px',
    fontSize: '14px',
    '&:focus': {
      backgroundColor: '#ffffff',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e0e0e0',
    borderWidth: '1px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#1976d2',
  },
});

// Custom styled MenuItem component with odd/even row styling
const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'index',
})<{ index: number }>(({ index }) => ({
  backgroundColor: index % 2 === 0 ? '#f5f5f5' : '#ffffff',
  '&:hover': {
    backgroundColor: '#e3f2fd',
  },
  '& .MuiListItemText-primary': {
    fontSize: '14px',
    color: '#333333',
  },
}));

// export const MultiSelectProviderFilter: GridFilterOperator[] = [
//   {
//     label: 'Select Providers', // Simplified label for clarity
//     value: 'multiSelectProviders',
//     requiresFilterValue: false,
//     getApplyFilterFn: (filterItem: GridFilterItem) => {
//       if (!filterItem.value || !Array.isArray(filterItem.value) || filterItem.value.length === 0) {
//         return null; // No filter applied if no values are selected
//       }
//       return (params: GridCellParams) => {
//         return filterItem.value.includes(params.value);
//       };
//     },
//     InputComponent: (props: GridFilterInputMultipleValueProps) => {
//       const { item, applyValue } = props;

//       return (
//         <StyledSelect
//           multiple
//           value={item.value || []}
//           onChange={(event) => {
//             applyValue({ ...item, value: event.target.value as string[] });
//           }}
//           renderValue={(selected) => (selected as string[]).join(', ')}
//           sx={{ height: '45px' }}
//           MenuProps={{
//             PaperProps: {
//               style: {
//                 border: '1px solid #e0e0e0',
//                 boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
//               },
//             },
//           }}
//         >
//           {Providers.map((provider, index) => (
//             <StyledMenuItem key={provider} value={provider} index={index}>
//               <Checkbox
//                 checked={item.value?.includes(provider) || false}
//                 size='small'
//                 color='primary'
//               />
//               <ListItemText primary={provider} />
//             </StyledMenuItem>
//           ))}
//         </StyledSelect>
//       );
//     },
//   },
// ];

export const useProviderFilterOperators = (): GridFilterOperator[] => {
  const { providers } = useProviders();

  return useMemo(() => {
    if (!providers.length) {
      return [PreFilterOption];
    }

    return [
      PreFilterOption,
      ...providers.map((providerName) => ({
        label: providerName,
        value: providerName,
        getApplyFilterFn: (filterItem: GridFilterItem) => {
          if (!filterItem.value) return null;
          return (params: GridCellParams) => {
            const cell = (params.value || '').toString().toLowerCase().trim();
            const filter = (filterItem.value || '').toString().toLowerCase().trim();
            return cell === filter;
          };
        },
      })),
    ];
  }, [providers]);
};

export const ProviderFilter: GridFilterOperator[] = [
  PreFilterOption,
  ...Providers.map((providerName) => ({
    label: providerName,
    value: providerName,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return (params: GridCellParams) => params.value === providerName;
    },
  })),
];
